import React, { useState, useRef, useEffect } from "react";
import GraphGlyph from "./GraphGlyph";
import "./GraphCatalogRail.css";

/**
 * The catalogue rail — replaces the old title <select> and the caret pair, and
 * gives the five regressions and the moment mix a picker they never had.
 *
 * The families keep their reading order and are separated by a rule, but carry
 * no headings: nine already-descriptive graph names plus a label and a grain
 * note per family made the rail read as busy.
 *
 * `variant="header"` is the mobile form. It renders inside the chart header as
 * the title itself — the old mobile toggle sat above the header and repeated
 * the title verbatim — and its menu floats over the top of the plot rather
 * than pushing the plot down the page.
 */
function GraphCatalogRail({
  families,
  availableGraphs,
  graphSelected,
  onSelect,
  totalCount,
  scoredCount,
  variant = "rail",
}) {
  const isHeaderPicker = variant === "header";
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedLabel =
    families
      .flatMap((family) => availableGraphs[family] || [])
      .find((graph) => graph.id === graphSelected)?.label || "Select a graph";

  // A floating menu has to close the way a menu does. The rail variant is
  // always open and needs neither listener.
  useEffect(() => {
    if (!isHeaderPicker || !open) return;

    const handlePointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isHeaderPicker, open]);

  const handleSelect = (id) => {
    onSelect(id);
    setOpen(false);
  };

  const renderGroups = () => (
    <div className="rail-groups">
      {families.map((family) => (
        <div key={family} className="rail-group">
          <div className="rail-group-items">
            {availableGraphs[family].map((graph) => {
              const isActive = graph.id === graphSelected;
              return (
                <button
                  key={graph.id}
                  type="button"
                  className={`rail-item ${isActive ? "active" : ""}`}
                  onClick={() => handleSelect(graph.id)}
                  title={isActive ? undefined : graph.explainer}
                  aria-pressed={isActive}
                >
                  <span className="rail-item-row">
                    <span className="rail-item-glyph">
                      <GraphGlyph shape={graph.glyph} />
                    </span>
                    <span className="rail-item-label">{graph.label}</span>
                    {graph.unit && (
                      <span className="rail-item-unit">{graph.unit}</span>
                    )}
                  </span>
                  {isActive && (
                    <span className="rail-item-explainer">
                      {graph.explainer}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  if (isHeaderPicker) {
    return (
      <div className="graph-catalog-rail header-picker" ref={wrapRef}>
        <button
          type="button"
          className="picker-toggle"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-haspopup="true"
        >
          <span className="picker-toggle-label">{selectedLabel}</span>
          <span className="picker-toggle-caret" aria-hidden="true">
            {open ? "▴" : "▾"}
          </span>
        </button>
        {open && <div className="picker-menu">{renderGroups()}</div>}
      </div>
    );
  }

  return (
    <div className="graph-catalog-rail">
      <div className="rail-header">
        <span className="rail-header-count">
          {totalCount} entries · {scoredCount} scored
        </span>
      </div>
      {renderGroups()}
    </div>
  );
}

export default GraphCatalogRail;
